/* eslint-disable no-magic-numbers */
import { SOCIAL_AD_TYPES } from "utils/constants";
import { KILO, MEGA } from "utils/common";

import { isURL, maxLength, requiredNotNull } from "widgets/forms/validators/validators";
import { creativeNameAllowedChars, creativeNameMaxLength } from "widgets/forms/validators/Creatives";
import { filesize, duration } from "widgets/forms/validators/Assets";
import {
	errorMessageReducer,
	conditionalValidator,
	makeDynamicConfig,
	requiredAsset,
	validateUnlessAdStudio
} from "./validators";
import { mergeLists, uniqueVariableName } from "social/validators/utils";

export function tiktokValidCharacters(value) {
	const REGEX = /\p{Extended_Pictographic}|[{}#]/giu; // Extended_Pictographic used for emojis https://www.unicode.org/reports/tr51/#Emoji_Characters
	if (REGEX.test(value)) {
		return `Invalid characters. Emojis, "{ }", and "#" not allowed`;
	}
}

export const TIKTOK_VIDEO_CONSTRAINTS = {
	MAX_LENGTH_LANDING_PAGE: 800,
	MAX_LENGTH_PRIMARY_TEXT: 100,

	VIDEO_ACCEPT: ".mp4",
	MAX_VIDEO_MB: 500,
	DURATION: { min: 5, max: 60 },
	VIDEO_DIMENSIONS: [
		{ aspectRatio: "9:16", minWidth: 540, minHeight: 960, tolerance: 0.01 },
		{ aspectRatio: "1:1", minWidth: 640, minHeight: 640, tolerance: 0.01 },
		{ aspectRatio: "16:9", minWidth: 960, minHeight: 540, tolerance: 0.01 },
	],

	THUMBNAIL_ACCEPT: ".jpg, .jpeg, .png",
	MAX_THUMBNAIL_KB: 500,
};

/**
 * Validate Constraints that have multiple dimensions
 * @param {Array<{aspectRatio: string, minWidth: Number, minHeight: Number, tolerance: Number}>} dimensions
 *  Each item in the array will be compared to the dimensions of the user's selected video and
 *  should contain at least one item in dimensional format:
 * 	[{ aspectRatio: string, minWidth: Number, minHeight: Number, tolerance: Number }, ...]
 * 	tolerance is optional, the default value is 0 unless defined
 * @returns {string | undefined}
 * returns undefined if validation passes or there isn't anything to validate
 *
 * returns a string as an error messsage if validation fails
 */
export const tiktokVideoDimensionsValidator = (dimensions = []) => {
	return (value) => {
		const { width = null, height = null } = value?.metadata || {};

		if(width === null || height === null || !dimensions.length){
			// There's no value, or we otherwise can't assess the dimensions to be able to validate
			return;
		}

		const valueAspectRatio = width / height;

		for (const d of dimensions) {
			const { minWidth, minHeight, tolerance = 0 } = d;
			const targetRatio = minWidth / minHeight;
			const low = targetRatio * (1 - tolerance);
			const high = targetRatio * (1 + tolerance);
			const matchedAspectRatio = valueAspectRatio >= low && valueAspectRatio <= high;

			if (
				matchedAspectRatio
				&& width >= minWidth
				&& height >= minHeight
			) {
				return;
			}
		}

		return getVideoDimensionArrayMessage(dimensions);
	};
};

export const TIKTOK_VIDEO_VALIDATORS = {
	video: validateUnlessAdStudio([
		requiredAsset,
		filesize({ max: TIKTOK_VIDEO_CONSTRAINTS.MAX_VIDEO_MB * MEGA }),
		duration(TIKTOK_VIDEO_CONSTRAINTS.DURATION),
		tiktokVideoDimensionsValidator(TIKTOK_VIDEO_CONSTRAINTS.VIDEO_DIMENSIONS),
	]),
	thumbnail: validateUnlessAdStudio([
		filesize({ max: TIKTOK_VIDEO_CONSTRAINTS.MAX_THUMBNAIL_KB * KILO }),
	]),
	landingPage: [
		conditionalValidator("cta", requiredNotNull, cta => cta?.value),
		isURL,
		maxLength(TIKTOK_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	primaryText: [
		requiredNotNull,
		tiktokValidCharacters,
		maxLength(TIKTOK_VIDEO_CONSTRAINTS.MAX_LENGTH_PRIMARY_TEXT),
	],
	cta: [
		conditionalValidator("landingPage", requiredNotNull, landingPage => landingPage?.value),
	],
};

const TIKTOK_VIDEO_WARNINGS = mergeLists(
	{
		video: [uniqueVariableName],
		thumbnail: [uniqueVariableName],
		primaryText: [uniqueVariableName],
		landingPage: [uniqueVariableName],
		cta: [uniqueVariableName],
	},
	makeDynamicConfig(TIKTOK_VIDEO_VALIDATORS)
);

/**
 * Converts an array of dimensions into a readable format which can be used for
 * the requirement helper text or an error message
 * @param {Array<{aspectRatio: string, minWidth: number, minHeight: number}>} dimensions
 * Each item in the array will be parsed into a string into a readable format
 * @returns {string}
 */
export const getVideoDimensionArrayMessage = (dimensions = []) => {
	return dimensions.reduce((prev, current) => {
		const requirement = `${current.aspectRatio} minimum ${current.minWidth}x${current.minHeight}px`;

		if (prev === "") {
			return requirement;
		}

		return `${prev} or ${requirement}`;
	}, "");
};

export function tiktokVideoWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.TIKTOK_VIDEO;
	const values = allValues[formName] || {};

	if (!values.isDynamic) {
		return errorMessageReducer(TIKTOK_VIDEO_WARNINGS, values, formName);
	}
}

export function tiktokVideoValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.TIKTOK_VIDEO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if (values.isDynamic) {
		validators = {
			...validators,
			...TIKTOK_VIDEO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}
